import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { Save, Bot, Sparkles, Cpu, Info } from 'lucide-react';

const EMERGENT_MODELS = {
  openai: ['gpt-5.4', 'gpt-5.4-mini', 'gpt-5.2', 'gpt-5', 'gpt-4o-mini', 'gpt-4o'],
  anthropic: ['claude-sonnet-4-6', 'claude-haiku-4-5-20251001', 'claude-opus-4-6'],
  gemini: ['gemini-3.1-pro-preview', 'gemini-3-flash-preview', 'gemini-2.5-pro', 'gemini-2.5-flash'],
};

export default function AdminChatbot() {
  const [form, setForm] = useState({
    enabled: false, provider: 'catalog',
    emergentProvider: 'openai', emergentModel: 'gpt-5.4',
    ollamaUrl: 'http://localhost:11434', ollamaModel: 'llama3',
    systemPrompt: '', welcomeMessage: '',
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try { const d = await api.admin.llmGet(); setForm(f => ({ ...f, ...d })); } catch {}
    })();
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.admin.llmUpdate(form);
      toast({ title: 'Chatbot settings saved', description: form.enabled ? 'Bot is live on the site.' : 'Bot is disabled.' });
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold flex items-center gap-2"><Bot size={26} className="text-[#6b47dc]" /> AI Chatbot</h1>
        <p className="text-gray-500 text-sm mt-1">Choose your LLM provider and configure the assistant that appears on every page.</p>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-4">
        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-100 hover:border-purple-200">
          <input type="checkbox" className="accent-[#6b47dc] w-5 h-5" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          <div>
            <div className="font-bold">Enable chatbot widget</div>
            <div className="text-xs text-gray-500">Shows a floating chat bubble in the bottom-right corner of every public page.</div>
          </div>
        </label>

        <div>
          <label className="text-xs font-bold text-gray-500 mb-2 block">LLM provider</label>
          <div className="grid md:grid-cols-3 gap-3">
            <label className={`p-4 rounded-xl border-2 cursor-pointer ${form.provider === 'catalog' ? 'border-[#6b47dc] bg-purple-50' : 'border-gray-200'}`}>
              <input type="radio" name="provider" checked={form.provider === 'catalog'} onChange={() => setForm({ ...form, provider: 'catalog' })} className="hidden" />
              <div className="flex items-center gap-2 mb-1"><Bot size={16} className="text-[#6b47dc]" /> <span className="font-bold">Built-in catalog</span></div>
              <div className="text-xs text-gray-500">Works immediately from your live courses. No API key or model server required.</div>
            </label>
            <label className={`p-4 rounded-xl border-2 cursor-pointer ${form.provider === 'emergent' ? 'border-[#6b47dc] bg-purple-50' : 'border-gray-200'}`}>
              <input type="radio" name="provider" checked={form.provider === 'emergent'} onChange={() => setForm({ ...form, provider: 'emergent' })} className="hidden" />
              <div className="flex items-center gap-2 mb-1"><Sparkles size={16} className="text-[#6b47dc]" /> <span className="font-bold">Emergent provider</span></div>
              <div className="text-xs text-gray-500">Requires the optional emergentintegrations package and EMERGENT_LLM_KEY on the server.</div>
            </label>
            <label className={`p-4 rounded-xl border-2 cursor-pointer ${form.provider === 'ollama' ? 'border-[#6b47dc] bg-purple-50' : 'border-gray-200'}`}>
              <input type="radio" name="provider" checked={form.provider === 'ollama'} onChange={() => setForm({ ...form, provider: 'ollama' })} className="hidden" />
              <div className="flex items-center gap-2 mb-1"><Cpu size={16} className="text-[#ff5722]" /> <span className="font-bold">Ollama (self-hosted)</span></div>
              <div className="text-xs text-gray-500">Point to your own Ollama instance (private / offline / on-prem).</div>
            </label>
          </div>
        </div>

        {form.provider === 'catalog' ? (
          <div className="p-4 rounded-xl bg-purple-50/40 border border-purple-100 text-sm text-gray-600">
            The built-in assistant searches the course catalog and returns matching course recommendations. Enable the widget and save to make it live.
          </div>
        ) : form.provider === 'emergent' ? (
          <div className="grid md:grid-cols-2 gap-4 p-4 rounded-xl bg-purple-50/40 border border-purple-100">
            <div>
              <label className="text-xs font-bold text-gray-500 mb-1 block">Emergent sub-provider</label>
              <select value={form.emergentProvider} onChange={(e) => setForm({ ...form, emergentProvider: e.target.value, emergentModel: EMERGENT_MODELS[e.target.value][0] })} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc] bg-white">
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="gemini">Gemini</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 mb-1 block">Model</label>
              <select value={form.emergentModel} onChange={set('emergentModel')} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc] bg-white">
                {(EMERGENT_MODELS[form.emergentProvider] || []).map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 p-4 rounded-xl bg-orange-50/40 border border-orange-100">
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 mb-1 block">Ollama server URL</label>
              <input value={form.ollamaUrl || ''} onChange={set('ollamaUrl')} placeholder="http://your-ollama:11434" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#ff5722]" />
              <p className="text-[11px] text-gray-500 mt-1">Must be reachable from this server. If self-hosted on the same VPS use <span className="font-mono">http://localhost:11434</span>.</p>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 mb-1 block">Model name</label>
              <input value={form.ollamaModel || ''} onChange={set('ollamaModel')} placeholder="llama3, mistral, gemma3, phi3 …" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#ff5722]" />
              <p className="text-[11px] text-gray-500 mt-1">Whatever you have pulled via <span className="font-mono">ollama pull &lt;model&gt;</span>.</p>
            </div>
          </div>
        )}

        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block">Welcome message (first bubble shown to visitors)</label>
          <input value={form.welcomeMessage || ''} onChange={set('welcomeMessage')} placeholder="Hi! I can help you find the right course." className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
        </div>

        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block">System prompt (custom personality &amp; instructions)</label>
          <textarea rows={5} value={form.systemPrompt || ''} onChange={set('systemPrompt')} placeholder="You are the Skillbridge.guru AI assistant..." className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc] font-mono text-xs" />
          <p className="text-[11px] text-gray-500 mt-1">Leave blank to use the built-in system prompt (recommends courses from your live catalog).</p>
        </div>

        <Button onClick={save} disabled={saving} className="bg-[#6b47dc] hover:bg-[#5a3ac0]">
          <Save size={16} className="mr-1" /> {saving ? 'Saving...' : 'Save chatbot settings'}
        </Button>
      </div>

      <div className="bg-white p-5 rounded-2xl border border-gray-200 text-sm text-gray-600">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-[#6b47dc] mt-0.5" />
          <div>
            The chatbot has your <b>live course catalog</b> injected into every conversation, so it can recommend real courses with the correct prices, durations, and skills. Multi-turn history is stored in MongoDB under <span className="font-mono">chat_messages</span> so users can resume where they left off.
          </div>
        </div>
      </div>
    </div>
  );
}
